import { createFileRoute, notFound } from "@tanstack/react-router";
import BlogCategory from "@/pages/BlogCategory";
import { CATEGORY_SLUG_MAP } from "@/lib/categorySlug";
import { deduplicatePosts } from "@/lib/blogPostProcessor";
import { normalizeCategory } from "@/lib/categoryNormalizer";
import { allBlogPosts } from "@/lib/allBlogPosts";

const CATEGORY_TITLES: Record<string, { title: string; description: string }> =
  {
    "Compensation & Logistics": {
      title: "Compensation & Logistics for Remote Technical Teams",
      description:
        "Practical frameworks for paying, managing, and scaling remote technical talent across borders. Wise transfers, tax compliance, contractor agreements, and security protocols.",
    },
    "Hiring Strategy": {
      title: "Hiring Strategy for Technical Roles",
      description:
        "How to source, vet, and hire elite technical talent through talent arbitrage. Role definitions, interview frameworks, and delegation systems.",
    },
    "Systems Architecture": {
      title: "Systems Architecture for Revenue Operations",
      description:
        "Design patterns for building production-grade CRM, automation, and data infrastructure. Integration maps, reliability engineering, and scaling protocols.",
    },
    "Revenue Operations": {
      title: "Revenue Operations Engineering",
      description:
        "Build revenue engines that compound. Pipeline architecture, sales ops automation, analytics dashboards, and the metrics that actually predict growth.",
    },
    "Agency Scaling": {
      title: "Agency Scaling Infrastructure",
      description:
        "Scale an agency from 5 to 50 clients without breaking operations. Capacity planning, client delivery systems, and the automation that makes scaling linear.",
    },
    "Automation Strategy": {
      title: "Automation Strategy and Implementation",
      description:
        "Strategic frameworks for building automation that drives revenue, not just saves time. Client onboarding, sales automation, and workflow orchestration.",
    },
    "Hiring Guide": {
      title: "Hiring Guides for Technical Roles",
      description:
        "Step-by-step guides for hiring specific technical roles. Virtual assistants, automation specialists, CRM managers, and systems architects.",
    },
    "Advanced Automation": {
      title: "Advanced Automation Engineering",
      description:
        "Deep technical guides for complex automation. API orchestration, multi-step workflows, error handling, and production-grade system design.",
    },
    "Artificial Intelligence": {
      title: "Artificial Intelligence and Agentic Automation",
      description:
        "Practical AI automation for business operations. Voice AI agents, agentic workflows, AI-powered content systems, and the next generation of automation.",
    },
    "Conversion Optimization": {
      title: "Conversion Optimization Engineering",
      description:
        "Engineering conversion at every stage. Lead capture systems, funnel optimization, behavioral design, and the psychology behind high-converting pages.",
    },
    "CRM Architecture": {
      title: "CRM Architecture and Optimization",
      description:
        "Design and optimize CRM systems for scale. Database architecture, data hygiene, pipeline design, and the HubSpot and GHL optimization patterns that work.",
    },
    "Management & Tracking": {
      title: "Management and Tracking Systems",
      description:
        "Systems for managing remote teams and tracking performance. Time tracking, productivity metrics, communication protocols, and performance dashboards.",
    },
    "Infrastructure Scaling": {
      title: "Infrastructure Scaling for Growing Teams",
      description:
        "Scale your technical infrastructure as your team grows. Company wikis, training systems, tool stacks, and the infrastructure that supports 50+ person teams.",
    },
    "GHL for Filipino VAs": {
      title: "GHL for Filipino Virtual Assistants",
      description:
        "Complete GHL tutorials, CRM setup guides, and career resources for aspiring VAs in the Philippines. Learn GoHighLevel from scratch and land your first international client.",
    },
  };

const dedupedPosts = deduplicatePosts(allBlogPosts);

export const Route = createFileRoute("/blog/category/$categorySlug")({
  loader: ({ params }) => {
    const categoryName = CATEGORY_SLUG_MAP[params.categorySlug] || "";
    if (!categoryName) throw notFound();
    return { categoryName, categorySlug: params.categorySlug };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Category not found | Marjohn Robillo Blog" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { categoryName, categorySlug } = loaderData;
    const data = CATEGORY_TITLES[categoryName];
    if (!data) {
      return {
        meta: [
          { title: "Category not found | Marjohn Robillo Blog" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const baseTitle = data.title;
    const fullTitle = `${baseTitle} | Marjohn Robillo Blog`;
    const displayTitle = fullTitle.length > 60 ? baseTitle : fullTitle;
    const url = `https://marjohnrobillo.me/blog/category/${categorySlug}`;
    const ogImage =
      "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png";

    const categoryPosts = dedupedPosts.filter(
      (post) => normalizeCategory(post.category) === categoryName,
    );

    return {
      meta: [
        { title: displayTitle },
        { name: "description", content: data.description },
        {
          name: "robots",
          content:
            "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        {
          name: "keywords",
          content: `${data.title}, ${categoryName}, Marjohn Robillo blog, automation, revenue operations`,
        },
        { property: "og:title", content: displayTitle },
        { property: "og:description", content: data.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: displayTitle },
        { name: "twitter:description", content: data.description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: data.title,
            description: data.description,
            url,
            isPartOf: {
              "@type": "WebSite",
              name: "Marjohn Robillo Blog",
              url: "https://marjohnrobillo.me/blog",
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: categoryPosts.length,
              itemListElement: categoryPosts
                .slice(0, 20)
                .map((post, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  url: `https://marjohnrobillo.me/blog/${post.slug}`,
                  name: post.title,
                })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://marjohnrobillo.me",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Blog",
                item: "https://marjohnrobillo.me/blog",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: categoryName,
                item: url,
              },
            ],
          }),
        },
      ],
    };
  },
  component: BlogCategory,
});
